import { useMemo, useRef, useState } from 'react'
import { Package } from 'lucide-react'

import type { Product } from '@/lib/api/product-api'
import { useDebounced } from '@/hooks/use-debounced'
import { useProduct, useProducts } from '@/hooks/use-product-query'
import { Combobox } from '@/components/ui/combobox'
import { formatQty } from '@/lib/format'
import { getProductImageURL } from '@/lib/product-image'
import { m } from '@/lib/paraglide/messages'

/** Delay keystroke → API fire so typing "combo" doesn't hit /products 5 times. */
const SEARCH_DEBOUNCE_MS = 300

interface Props {
    value: string
    /** Emits the selected product's id. The full `Product` object is passed as
     *  a second arg so callers that need to seed sibling fields (unit_price,
     *  name, ...) can do so without a separate lookup. */
    onChange: (productId: string, product?: Product) => void
    /** When set, shows the "Số lượng: X" badge below the combobox once a product
     *  is selected (stock comes from product.stock_available on the list, no
     *  separate fetch). Omit for flows that don't want the selected-stock line. */
    warehouseId?: string
    className?: string
    placeholder?: string
    /** Optional search fanout — some callers use it for analytics or custom
     *  server-side filtering. Ignored for the default in-memory filter. */
    onSearchChange?: (search: string) => void
    /** Product ids to hide from the dropdown — e.g. products already added to a
     *  recipe/discount table. The currently selected `value` is never hidden. */
    excludeProductIds?: string[]
    /** Products to always keep in the options regardless of the current search
     *  result — e.g. a barcode-scanned product that lives outside the search
     *  page. Merged (and de-duped by id) with the fetched list. */
    extraProducts?: Product[]
    /** Show a clear (×) button to reset the selection — used when the picker
     *  acts as an optional list filter ("all products" = empty value). */
    allowClear?: boolean
}

/**
 * Render dropdown row cho 1 SP: image 40x40 (fallback icon Package) +
 * 2 dòng (name / SKU · KLT · unit). Skip KLT khi weight_value ≤ 0.
 *
 * Exported để mobile/tablet form và các picker khác reuse cùng layout.
 */
export function renderProductOption(product: Product): React.ReactNode {
    const primary =
        product.images?.find((i) => i.is_primary) ?? product.images?.[0]
    const parts: string[] = [product.sku]
    if (product.weight_value && product.weight_value > 0) {
        parts.push(`${product.weight_value} ${product.weight_unit ?? 'kg'}`)
    }
    parts.push(product.unit)
    const meta = parts.join(' · ')

    const stock = product.stock_available ?? 0
    const outOfStock = stock <= 0

    return (
        <div className="flex w-full items-center gap-3">
            {primary ? (
                <img
                    src={getProductImageURL(primary.image_url)}
                    alt={product.name}
                    className="size-10 rounded-md object-cover flex-shrink-0"
                />
            ) : (
                <div className="size-10 rounded-md bg-muted flex items-center justify-center flex-shrink-0">
                    <Package className="size-5 text-muted-foreground" />
                </div>
            )}
            <div className="flex-1 min-w-0">
                <div className="text-sm font-medium break-words">
                    {product.name}
                </div>
                <div className="text-xs text-muted-foreground truncate">
                    {meta}
                </div>
            </div>
            <span
                className={
                    outOfStock
                        ? 'ml-auto flex-shrink-0 text-right text-xs font-medium text-destructive'
                        : 'ml-auto flex-shrink-0 text-right text-xs font-medium text-muted-foreground'
                }
            >
                {m.product_option_stock({ qty: formatQty(stock) })}
            </span>
        </div>
    )
}

/**
 * ProductPicker — reusable single-product combobox used by order/POS/stock-out
 * flows. Encapsulates the product list fetch + option rendering + selected
 * stock display so callsites only pass `value` and `onChange`. Dropdown option
 * hiển thị image + tên + SKU · KLT · unit; trigger hiển thị chỉ tên SP.
 */
export function ProductPicker({
    value,
    onChange,
    warehouseId,
    className,
    placeholder,
    onSearchChange,
    excludeProductIds,
    extraProducts,
    allowClear,
}: Props) {
    const [search, setSearch] = useState('')
    const debouncedSearch = useDebounced(search, SEARCH_DEBOUNCE_MS)
    const { data: productsRes } = useProducts({
        // Cap the initial fetch so the dropdown stays fast; users searching
        // beyond the cap trigger the server-side filter via ?search=<q>.
        per_page: 200,
        // Picker chỉ cho chọn SP đang bán — SP ngừng bán (is_active=false) bị
        // loại ở tầng BE qua param này. SP đã ngừng bán mà đang được chọn sẵn
        // vẫn hiển thị nhờ fetch-by-id bên dưới (GetByID không lọc is_active).
        is_active: true,
        ...(debouncedSearch ? { search: debouncedSearch } : {}),
    })
    const fetched: Product[] = productsRes?.data ?? []

    // Resolve SP đang chọn theo id để nhãn không biến mất khi value là SP KHÔNG
    // có trong danh sách active ở trên — vd sửa đơn cũ có SP đã ngừng bán, hoặc
    // SP nằm ngoài trang search. GetByID không lọc is_active nên luôn resolve
    // được; enabled tự tắt khi value rỗng.
    const { data: selectedById } = useProduct(value)

    // Remember every product we've resolved so the selected one can still be
    // shown in the trigger after the search query changes and no longer
    // returns it. Server search caps the list, so without this the selected
    // label vanishes to the placeholder the moment the user searches elsewhere.
    const seenRef = useRef<Map<string, Product>>(new Map())
    for (const p of fetched) seenRef.current.set(p.id, p)
    if (extraProducts) {
        for (const p of extraProducts) seenRef.current.set(p.id, p)
    }
    // GetByID (useProduct) KHÔNG trả stock_available — BE serialize về 0 (xem
    // product_repository.go: chỉ List/GetByBarcode có cột tồn). Nó chỉ dùng để
    // resolve NHÃN cho SP không nằm trong list (vd SP ngừng bán ở đơn cũ). CHỈ
    // ghi khi chưa có bản nào: nếu ghi đè bản từ list (đã có tồn) thì badge
    // "Số lượng" sẽ hiển thị 0 dù sản phẩm còn hàng.
    if (selectedById && !seenRef.current.has(selectedById.id)) {
        seenRef.current.set(selectedById.id, selectedById)
    }

    const selected = value ? seenRef.current.get(value) : undefined

    const options = useMemo(() => {
        const exclude = new Set(excludeProductIds)
        // Current search results + always-on extras + the selected product,
        // de-duped by id. Excluded ids drop out, but never the selected value.
        const map = new Map<string, Product>()
        for (const p of fetched) map.set(p.id, p)
        if (extraProducts) {
            for (const p of extraProducts) map.set(p.id, p)
        }
        if (selected) map.set(selected.id, selected)
        return Array.from(map.values())
            .filter((p) => p.id === value || !exclude.has(p.id))
            .map((p) => ({
                value: p.id,
                label: `${p.name} (${p.unit})`,
                render: () => renderProductOption(p),
            }))
    }, [fetched, extraProducts, selected, excludeProductIds, value])

    const selectedStock = selected?.stock_available

    const handleSearchChange = (q: string) => {
        setSearch(q)
        onSearchChange?.(q)
    }

    const handleChange = (productId: string) => {
        const picked = seenRef.current.get(productId)
        onChange(productId, picked)
    }

    // Ô tìm kiếm nằm trong popover bị Radix unmount khi đóng; reset state search
    // theo, nếu không mở lại thấy ô rỗng mà danh sách vẫn lọc theo từ khoá cũ.
    const handleOpenChange = (open: boolean) => {
        if (!open) {
            setSearch('')
            onSearchChange?.('')
        }
    }

    return (
        <div className={className}>
            <Combobox
                value={value}
                options={options}
                onChange={handleChange}
                onSearchChange={handleSearchChange}
                onOpenChange={handleOpenChange}
                shouldFilter={false}
                allowClear={allowClear}
                placeholder={
                    placeholder ?? m.order_select_product_placeholder()
                }
                searchPlaceholder={m.order_select_product_placeholder()}
                emptyText={m.product_not_found()}
            />
            {value && selected && warehouseId && (
                <p
                    className={
                        typeof selectedStock === 'number' && selectedStock <= 0
                            ? 'mt-1 text-xs text-destructive'
                            : 'mt-1 text-xs text-muted-foreground'
                    }
                >
                    {m.order_product_stock_badge({
                        qty: formatQty(selectedStock ?? 0),
                        unit: selected.unit,
                    })}
                </p>
            )}
        </div>
    )
}
